import React, { useCallback, useEffect, useState, Component } from 'react';
import { Activity, ShieldCheck, Sliders, Table2, RefreshCw, AlertTriangle, Camera, GitCompare, GraduationCap, BookOpenCheck } from 'lucide-react';
import { PageLayout } from '../../shared/PageLayout';
import { AdminTabs } from '../shared/AdminTabs';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { Button } from '../../../components/ui/Button';
import { RulesTab, GovernanceSettingsTab, useGovernanceData, OverviewDashboard, QuestionsTab, CompareSnapshotsTab } from '../content/governance';
import { AssessmentPoolHealthTab } from '../content/governance/tabs/AssessmentPoolHealthTab';
import { PracticeContentHealthTab } from '../content/governance/tabs/PracticeContentHealthTab';
import { SaveSnapshotModal } from '../content/governance/tabs/SaveSnapshotModal';
import type { AdminNavigationContext, AdminRoute } from '../adminNavigation';
import { readAllowedSearchParam, readSearchParam, replaceSearchParams } from '../adminUrlState';
import { isVisualPolicyDegraded } from '../../../governance/pipeline/policyEngine';
import { createSnapshot, saveSnapshot } from '../../../governance/pipeline/snapshots';
// ─── Types & Constants ────────────────────────────────────────────────────────
type TabId = 'overzicht' | 'vragen' | 'vergelijken' | 'toetsen' | 'oefenstof' | 'regels' | 'instellingen';
const TABS = [{
  id: 'overzicht',
  label: 'Overzicht',
  icon: <Activity className="w-4 h-4" data-id="element-7152" data-mp="bpw5uxiggbx1" />
}, {
  id: 'vragen',
  label: 'Vragen',
  icon: <Table2 className="w-4 h-4" data-id="element-7153" data-mp="goh7n1dcv3nl" />
}, {
  id: 'vergelijken',
  label: 'Vergelijken',
  icon: <GitCompare className="w-4 h-4" data-id="element-7154" data-mp="lobxd1le9n72" />
}, {
  id: 'toetsen',
  label: 'Toetsen',
  icon: <GraduationCap className="w-4 h-4" data-id="element-7155" data-mp="qner3pvxc6zq" />
}, {
  id: 'oefenstof',
  label: 'Oefenstof',
  icon: <BookOpenCheck className="w-4 h-4" data-id="element-7156" data-mp="2nfyjaoqfirwo" />
}, {
  id: 'regels',
  label: 'Regels',
  icon: <ShieldCheck className="w-4 h-4" data-id="element-7157" data-mp="2nib9fp6cc8kt" />
}, {
  id: 'instellingen',
  label: 'Instellingen',
  icon: <Sliders className="w-4 h-4" data-id="element-7158" data-mp="2sgw99nhokq8q" />
}] as const;
const TAB_IDS = TABS.map(tab => tab.id) as readonly TabId[];
const TAB_DESCRIPTIONS: Record<TabId, string> = {
  overzicht: 'Systeemgezondheid, top blockers en module status op een oogopslag.',
  vragen: 'Eén centrale tabel met alle vragen, validatieresultaten, quality scores en seed issues.',
  vergelijken: 'Vergelijk twee pipeline runs om regressies of verbeteringen te spotten.',
  toetsen: 'Toets-feasibility per groep × module: kan de blueprint-generator een volledige Cito-style toets opleveren?',
  oefenstof: 'Oefenstof-capaciteit per groep en family: waar dreigen herhaling, fallback of leerdoel-drift?',
  regels: 'Alle validatieregels, regelboeken en drempelwaarden — de complete governance-referentie.',
  instellingen: 'Configureer kwaliteitspoorten, pipeline-fasen en beleidsregels.'
};
// ─── Health Status ────────────────────────────────────────────────────────────
const HEALTH_CONFIG = {
  pending: {
    label: 'Niet uitgevoerd',
    color: 'text-gray-600',
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    dot: 'bg-gray-400'
  },
  ok: {
    label: 'Gezond',
    color: 'text-emerald-700',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    dot: 'bg-emerald-500'
  },
  warning: {
    label: 'Aandacht vereist',
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    dot: 'bg-amber-500'
  },
  error: {
    label: 'Kritieke fouten',
    color: 'text-red-700',
    bg: 'bg-red-50',
    border: 'border-red-200',
    dot: 'bg-red-500'
  }
};
function getHealthStatus(fail: number, review: number, isRunning: boolean) {
  if (isRunning) return 'pending';
  if (fail > 0) return 'error';
  if (review > 0) return 'warning';
  return 'ok';
}
// ─── Main Page Component ──────────────────────────────────────────────────────
interface GovernancePageProps {
  navigationContext?: AdminNavigationContext | null;
  onNavigate?: (route: AdminRoute, context?: AdminNavigationContext) => void;
}
export function GovernancePage({
  navigationContext,
  onNavigate
}: GovernancePageProps) {
  const readUrlState = useCallback(() => {
    const filter = readSearchParam('filter');
    return {
      activeTab: filter ? 'vragen' : readAllowedSearchParam('tab', TAB_IDS, 'overzicht'),
      questionFilter: filter
    };
  }, []);
  const [activeTab, setActiveTab] = useState<TabId>(() => readUrlState().activeTab);
  const [questionFilter, setQuestionFilter] = useState<string | undefined>(() => readUrlState().questionFilter);
  const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
  const [snapshotRefreshTrigger, setSnapshotRefreshTrigger] = useState(0);
  // R8 (Sprint 5): surface degraded visual-policy state so admins know that
  // visual answer-leakage checks may be running on the empty default policy.
  const visualPolicyDegraded = isVisualPolicyDegraded();
  const {
    rows,
    stats,
    systemStats,
    moduleHealth,
    familyHealthByModule,
    questionTypeDistribution,
    visualTypeDistribution,
    topBlockers,
    familyTriage,
    assessmentPoolHealth,
    practiceContentHealth,
    pipelineIssueCounts,
    isRunning,
    lastRun,
    batchResult,
    revalidate,
    shadowImpactReport
  } = useGovernanceData();
  const overallHealth = getHealthStatus(stats.fail, stats.review, isRunning);
  const hcfg = HEALTH_CONFIG[overallHealth];
  const applyQuestionContext = useCallback((filter?: string) => {
    setQuestionFilter(filter);
    setActiveTab('vragen');
    replaceSearchParams({
      tab: 'vragen',
      filter
    });
  }, []);
  useEffect(() => {
    const handlePopState = () => {
      const nextState = readUrlState();
      setActiveTab(nextState.activeTab);
      setQuestionFilter(nextState.questionFilter);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [readUrlState]);
  useEffect(() => {
    if (navigationContext?.kind !== 'governance-question') return;
    applyQuestionContext(navigationContext.questionId);
  }, [applyQuestionContext, navigationContext]);
  const handleNavigateToQuestion = (filter?: string) => {
    applyQuestionContext(filter);
  };
  const handleTabChange = (id: string) => {
    const nextTab = id as TabId;
    const nextFilter = nextTab === 'vragen' ? questionFilter : undefined;
    setActiveTab(nextTab);
    setQuestionFilter(nextFilter);
    replaceSearchParams({
      tab: nextTab,
      filter: nextFilter
    });
  };
  const handleSaveSnapshot = (label: string, note: string) => {
    if (!batchResult) return;
    const snap = createSnapshot(batchResult, {
      label: label || undefined,
      note: note || undefined
    });
    const success = saveSnapshot(snap);
    if (success) {
      setSnapshotRefreshTrigger(prev => prev + 1);
      // In a real app we'd use a toast here
      alert('Snapshot succesvol opgeslagen!');
    } else {
      alert('Opslaan mislukt — localStorage vol?');
    }
  };
  return <PageLayout title="Governance & Kwaliteit" subtitle="Kwaliteitsbeheer, validatie en analyse van het vragenbestand" actions={<div className="flex items-center gap-3" data-id="element-7160" data-mp="1vrj6li3re9il">
          {/* Health Status Indicator */}
          <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg border ${hcfg.border} ${hcfg.bg}`} data-id="element-7161" data-mp="1i0nqpluues5i">
          
            <div className={`w-2 h-2 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-7162" data-mp="1mzh31aye8d43" />
          
            <span className={`text-xs font-medium ${hcfg.color}`} data-id="element-7163" data-mp="2amp75pk35g4j">
              {hcfg.label}
            </span>
          </div>

          <Button variant="secondary" onClick={() => setIsSnapshotModalOpen(true)} disabled={isRunning || !batchResult} data-id="element-7164" data-mp="20qvrvyezf8j2">
          
            <Camera className="w-4 h-4 mr-2" data-id="element-7165" data-mp="25nvufzcmht5f" />
            Snapshot opslaan
          </Button>

          {/* Re-validate Button */}
          <Button variant="primary" onClick={revalidate} disabled={isRunning} data-id="element-7166" data-mp="pdjr5weir614">
            {isRunning ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" data-id="element-7167" data-mp="u87alguo1a8u" /> : <RefreshCw className="w-4 h-4 mr-2" data-id="element-7168" data-mp="1lywey0anfkvw" />}
            {isRunning ? 'Valideren...' : 'Hervalideer'}
          </Button>

          <ConnectionBadge status="live" data-id="element-7169" data-mp="1gxgtw88h1edi" />
        </div>} breadcrumbs={[{
    label: 'Inhoud'
  }, {
    label: 'Governance & Kwaliteit'
  }]} data-id="element-7159" data-mp="1r0swjrke1ihk">
      
      {/* R8: Visual-policy degraded banner */}
      {visualPolicyDegraded && <div role="alert" className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 mb-4" data-id="element-7170" data-mp="172yngvbo74q0">
        
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" data-id="element-7171" data-mp="1zpjc3o0rh2pv" />
          <div className="text-sm" data-id="element-7172" data-mp="1uyjj2turj4sn">
            <p className="font-semibold text-red-800" data-id="element-7173" data-mp="1uxrpfokq7h50">
              Visuele policy gedegradeerd
            </p>
            <p className="text-red-700 mt-1" data-id="element-7174" data-mp="1pyoreo35yb6j">
              De visual policy kon niet uit de seeds worden geladen (build
              mislukte 3×). Visual-stage answer-leakage checks draaien tegen de
              lege default policy — visual-issues kunnen onvolledig zijn. Een
              app-restart kan dit oplossen.
            </p>
          </div>
        </div>}

      {/* Health Banner (Mobile) */}
      <div className={`sm:hidden flex items-center justify-between gap-3 rounded-xl border ${hcfg.border} ${hcfg.bg} p-3 mb-3`} data-id="element-7175" data-mp="2uz4ukvg91473">
        
        <div className="flex items-center gap-2" data-id="element-7176" data-mp="2zzsts4emsrce">
          <div className={`w-2.5 h-2.5 rounded-full ${hcfg.dot} ${overallHealth !== 'pending' ? 'animate-pulse' : ''}`} data-id="element-7177" data-mp="34xkof66nurka" />
          
          <span className={`text-sm font-medium ${hcfg.color}`} data-id="element-7178" data-mp="39tjhh72xoxr1">
            {hcfg.label}
          </span>
        </div>
        {lastRun && <span className="text-xs text-gray-500" data-id="element-7179" data-mp="3io7n4ycf8hgd">{lastRun}</span>}
      </div>

      <AdminTabs tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} data-id="element-7180" data-mp="3iqaa19ia1ugv" />
      

      <p className="text-sm text-gray-500 mb-5" data-id="element-7181" data-mp="3np4z2t6u8f9f">
        {TAB_DESCRIPTIONS[activeTab]}
      </p>

      <div data-id="element-7182" data-mp="3sk0vs6n0rqsv">
        {activeTab === 'overzicht' && <OverviewDashboard rows={rows} stats={stats} systemStats={systemStats} moduleHealth={moduleHealth} familyHealthByModule={familyHealthByModule} questionTypeDistribution={questionTypeDistribution} visualTypeDistribution={visualTypeDistribution} topBlockers={topBlockers} familyTriage={familyTriage} pipelineIssueCounts={pipelineIssueCounts} shadowImpactReport={shadowImpactReport} isRunning={isRunning} onNavigateToQuestion={handleNavigateToQuestion} data-id="element-7183" data-mp="1faqu4jz2h1i" />}
        {activeTab === 'vragen' && <QuestionsTab rows={rows} batchResult={batchResult} isRunning={isRunning} initialFilter={questionFilter} onOpenQuestionInBank={row => onNavigate?.('question-bank', {
        kind: 'question-bank-question',
        familyId: row.familyId,
        questionId: row.questionId
      })} data-id="element-7184" data-mp="1phrr3hfj4pa0" />}
        {activeTab === 'vergelijken' && <CompareSnapshotsTab refreshTrigger={snapshotRefreshTrigger} data-id="element-7185" data-mp="23g80xh8ouvwd" />}
        {activeTab === 'toetsen' && <AssessmentPoolHealthTab report={assessmentPoolHealth} isRunning={isRunning} onSelectQuestion={handleNavigateToQuestion} data-id="element-7186" data-mp="4587ydth61y3" />}
        {activeTab === 'oefenstof' && <PracticeContentHealthTab report={practiceContentHealth} isRunning={isRunning} data-id="element-7187" data-mp="1jqt7md003gx" />}
        {activeTab === 'regels' && <RulesTab data-id="element-7188" data-mp="3sxwgjgarqcqe" />}
        {activeTab === 'instellingen' && <GovernanceSettingsTab data-id="element-7189" data-mp="3swbmrswa2xtl" />}
      </div>

      <SaveSnapshotModal isOpen={isSnapshotModalOpen} onClose={() => setIsSnapshotModalOpen(false)} onSave={handleSaveSnapshot} data-id="element-7190" data-mp="3nys66mc3y8yh" />
      
    </PageLayout>;
}